import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { Users } from '@sorare/core/src/atoms/icons/Users.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconAddressCard } from '@sorare/core/src/atoms/icons/system/iconAddressCard.ts';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandS } from '@sorare/core/src/atoms/typography/index.tsx';
import { S27Title } from '@sorare/core/src/atoms/ui/S27Title/index.tsx';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { SeriesBalance } from '@sorare/core/src/components/navigation/AppNavigation/UserCurrencies/SeriesBalance/index.tsx';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import type { HexColor } from '@sorare/core/src/style/types.ts';

import { MissionSection } from '#src/components/missions/MissionSection/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import { NeonRewardConfigs } from '#src/components/rewards/NeonRewards/NeonRewardConfigs/index.tsx';
import { useInviteShareMessage } from '#src/hooks/useInviteShareMessage.ts';

import { ReferralMission } from './ReferralMission/index.tsx';
import { ReferralMissionDetails } from './ReferralMissionDetails/index.tsx';
import { ReferralMissionDialog } from './ReferralMissionDialog/index.tsx';
import type {
  ConnectTaskDialogQuery,
  ConnectTaskDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  min-height: 100%;
  isolation: isolate;
`;
const HeaderWrapper = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
`;
const FooterWrapper = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  position: sticky;
  bottom: var(--double-unit);
  padding-top: var(--double-unit);
  margin-top: auto;
`;
const MobileContent = styled(Vertical).attrs({ gap: 4, center: true })`
  padding: var(--intermediate-unit) var(--double-unit);
`;

const ContentWrapper = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  z-index: 1;
  overflow: hidden;
  max-height: 90vh;
  width: min(calc(80 * var(--vw)), 820px);
`;
const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  overflow: hidden;
`;
const LeftPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow-y: auto;
  padding: var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
`;
const RightPanel = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
  z-index: 2;
`;
const RightPanelContent = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;
const LeftPanelContent = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--double-unit) 0;
`;

const StyledSpan = styled.span`
  color: var(--c-nd-600);
`;

const CONNECT_TASK_DIALOG_QUERY = gql`
  query ConnectTaskDialogQuery($sport: Sport!) {
    cardSet {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
      currentSet(sport: $sport) {
        slug
        branding {
          id
          colors {
            surfacePrimary
            surfacePrimaryText
            surfaceSecondaryText
          }
          setIcon: assetUrl(slot: BRANDING_SET_ICON_MOBILE)
        }
      }
    }
    currentUser {
      slug
      referrerCode
      inviteTasksAsReferrer(sport: $sport) {
        id
        ...ReferralMission_inviteTaskAsReferrer
        ...ReferralMissionDialog_inviteTaskAsReferrer
        ... on InviteTaskAsReferrer {
          id
          steps {
            id
            aasmState
            rewardConfigs {
              id
              ...NeonRewardConfigs_rewardConfig
            }
          }
        }
      }
    }
  }
  ${NeonRewardConfigs.fragments.rewardConfig}
  ${ReferralMission.fragments.inviteTaskAsReferrer}
  ${ReferralMissionDialog.fragments.inviteTaskAsReferrer}
` as TypedDocumentNode<ConnectTaskDialogQuery, ConnectTaskDialogQueryVariables>;

type Props = {
  open: boolean;
  onClose: () => void;
};
export const ConnectTaskDialog = ({ open, onClose }: Props) => {
  const { sport } = useSportContext();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const { up: isLaptop } = useScreenSize('laptop');
  const {
    onClaim,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const { data, refetch } = useQuery(CONNECT_TASK_DIALOG_QUERY, {
    variables: { sport },
    skip: !open,
  });

  const { currentUser, cardSet } = data || {};
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const colors = cardSet?.currentSet?.branding?.colors;
  const surfacePrimary = (colors?.surfacePrimary ??
    'var(--c-nd-600-opaque)') as HexColor;
  const surfacePrimaryText = (colors?.surfacePrimaryText ??
    'var(--c-nd-600-opaque)') as HexColor;
  const surfaceSecondaryText = (colors?.surfaceSecondaryText ??
    'var(--c-nd-600-opaque)') as HexColor;
  const { referrerCode } = currentUser || {};
  const inviteShareMessage = useInviteShareMessage(referrerCode);

  const inviteTasks = (currentUser?.inviteTasksAsReferrer || []).filter(
    isTypeFn('InviteTaskAsReferrer')
  );
  const claimNowInviteTasks = inviteTasks.filter(task =>
    task.steps?.some(
      step => step.aasmState === ManagerProgressionTaskState.COMPLETED
    )
  );
  const claimedInviteTasks = inviteTasks.filter(
    task =>
      !claimNowInviteTasks.includes(task) &&
      task.aasmState === ManagerProgressionTaskState.CLAIMED
  );
  const ongoingInviteTasks = inviteTasks.filter(
    task =>
      !claimNowInviteTasks.includes(task) && !claimedInviteTasks.includes(task)
  );

  const friendsRemaining =
    (maximumInviteTasksAsReferrer || 10) - inviteTasks.length;

  if (isLaptop && !selectedTaskId && inviteTasks.length > 0 && open) {
    setSelectedTaskId(inviteTasks[0].id);
  }

  const selectedTask = inviteTasks.find(t => t.id === selectedTaskId);

  const inviteButton = (
    <SocialShare
      message={inviteShareMessage}
      renderButton={({ onClick }) => (
        <Button size="large" color="primary" onClick={onClick}>
          <FormattedMessage
            defaultMessage="Invite friends <span>({friendsRemaining, number} left)</span>"
            values={{
              friendsRemaining,
              span: chunks => <StyledSpan>{chunks}</StyledSpan>,
            }}
          />
        </Button>
      )}
      trackingEventName={socialShareEventName.SHARE_INVITE_FRIENDS_SERIES}
      trackingEventContext={socialShareEventContext.SERIES_INVITE_FRIENDS}
    />
  );

  const rewardConfigs = inviteTasks.flatMap(task =>
    task.steps
      .filter(step => step.aasmState === ManagerProgressionTaskState.CLAIMED)
      .flatMap(step => step.rewardConfigs)
  );

  const listContent = (
    <>
      <Vertical gap={2} center>
        <BrandS color={isLaptop ? 'var(--c-white)' : 'var(--c-black)'}>
          <FormattedMessage defaultMessage="Your progress" />
        </BrandS>
        <Horizontal gap={2}>
          <Horizontal gap={0.5}>
            <Users width={20} height={20} color="var(--c-blue-300)" />
            <BrandS color={isLaptop ? 'var(--c-white)' : 'var(--c-black)'}>
              {inviteTasks.length}
            </BrandS>
          </Horizontal>
          <NeonRewardConfigs
            size={16}
            iconSize={2}
            color={isLaptop ? undefined : 'var(--c-black)'}
            displayLabels={false}
            compact
            rewardConfigs={rewardConfigs}
          />
        </Horizontal>
      </Vertical>

      <Vertical gap={3}>
        {claimNowInviteTasks.length > 0 && (
          <MissionSection
            icon={<FontAwesomeIcon icon={iconGift} />}
            iconColor="var(--c-white)"
            title={<FormattedMessage defaultMessage="Claim now" />}
          >
            {claimNowInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
                onClaimed={refetch}
                surfacePrimary={surfacePrimary}
                surfacePrimaryText={surfacePrimaryText}
              />
            ))}
          </MissionSection>
        )}

        {ongoingInviteTasks.length > 0 && (
          <MissionSection
            icon={<FontAwesomeIcon icon={iconAddressCard} />}
            iconColor="var(--c-white)"
            title={<FormattedMessage defaultMessage="Invited friends" />}
          >
            {ongoingInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
                surfacePrimary={surfacePrimary}
                surfacePrimaryText={surfacePrimaryText}
              />
            ))}
          </MissionSection>
        )}

        {claimedInviteTasks.length > 0 && (
          <MissionSection
            icon={<FontAwesomeIcon icon={iconCheck} />}
            iconColor="var(--c-white)"
            title={<FormattedMessage defaultMessage="Completed" />}
          >
            {claimedInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
                surfacePrimary={surfacePrimary}
                surfacePrimaryText={surfacePrimaryText}
              />
            ))}
          </MissionSection>
        )}
      </Vertical>
    </>
  );

  if (isLaptop) {
    return (
      <Dialog open={open} onClose={onClose} hideHeader>
        <ContentWrapper>
          <TwoPanelLayout>
            <LeftPanel>
              <LeftPanelContent>
                <Horizontal center>
                  <S27Title>
                    <FormattedMessage defaultMessage="Connect" />
                  </S27Title>
                </Horizontal>
                {listContent}
                {inviteButton}
              </LeftPanelContent>
            </LeftPanel>
            <RightPanel>
              <SecondaryPageBackground
                overlayStartColor="var(--c-surface-light)"
                overlayMidColor="var(--c-competition-contenter-secondary)"
                overlayEndColor="var(--c-competition-mls-tertiary)"
                contained
              />
              <CloseButtonWrapper>
                <Button
                  size="small"
                  onClick={onClose}
                  startIcon={<FontAwesomeIcon icon={iconX} />}
                  color="tertiary"
                />
              </CloseButtonWrapper>
              {!!selectedTask && (
                <RightPanelContent>
                  <ReferralMissionDetails
                    task={selectedTask}
                    surfacePrimary={surfacePrimary}
                    surfacePrimaryText={surfacePrimaryText}
                    surfaceSecondaryText={surfaceSecondaryText}
                  />
                </RightPanelContent>
              )}
            </RightPanel>
          </TwoPanelLayout>
        </ContentWrapper>
        <TaskClaimedDialogs
          pickerDialogTask={pickerDialogTask}
          onPickerDialogClose={() => {
            setPickerDialogTask(null);
          }}
          revealPackCardsDialogReward={revealPackCardsDialogReward}
          onRevealPackCardsDialogClose={() => {
            setRevealPackCardsDialogReward(null);
          }}
          rewardDialogTask={rewardDialogTask}
          onRewardDialogClose={() => {
            setRewardDialogTask(null);
          }}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xs" hideHeader>
      <Root>
        <SecondaryPageBackground
          overlayStartColor="var(--c-surface-light)"
          overlayMidColor="var(--c-competition-contenter-secondary)"
          overlayEndColor="var(--c-competition-mls-tertiary)"
        />
        <HeaderWrapper>
          <SBHorizontal className="p-1 pb-0">
            <Button
              size="medium"
              color="tertiary"
              startIcon={<FontAwesomeIcon icon={iconChevronLeft} />}
              onClick={onClose}
            />
            <SeriesBalance color="tertiary" />
          </SBHorizontal>
        </HeaderWrapper>
        <MobileContent>
          <S27Title color="var(--c-black)">
            <FormattedMessage defaultMessage="Connect" />
          </S27Title>

          {listContent}
        </MobileContent>

        <FooterWrapper>{inviteButton}</FooterWrapper>
      </Root>
      {!!selectedTask && (
        <ReferralMissionDialog
          open={!!selectedTask}
          onClose={() => setSelectedTaskId(null)}
          task={selectedTask}
          surfacePrimary={surfacePrimary}
          surfacePrimaryText={surfacePrimaryText}
          surfaceSecondaryText={surfaceSecondaryText}
        />
      )}
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
        }}
      />
    </Dialog>
  );
};
